import {translate} from "react-i18nify";

import * as actionTypes from "../../constants/actionTypes";
import * as componentTypes from "../../constants/componentTypes";
import * as handlers from "../../constants/handlers";
import * as methods from "../../constants/methods";
import * as resultCodes from "../../constants/resultCodes";
import * as customTabActions from "../../actions/customTabActions";
import * as formulaActions from "../../actions/formulaActions";
import * as globalActions from "../../actions/globalActions";
import * as navigationActions from "../../actions/navigationActions";
import * as ticketingActions from "../../actions/ticketingActions";
import * as messageHelper from "../../helpers/messageHelper";
import * as modelHelper from "../../helpers/modelHelper";
import * as responseHelper from "../../helpers/responseHelper";

export const processWebsocketResponse = (tabId, method, response, state) => {
    switch (method) {
        case handlers.GET_ALL_CUSTOM_FANDECKS:
            if (!responseHelper.isGetAllCustomFandecksFormatValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                {
                    type: actionTypes.SAVE_CUSTOM_FANDECK_LIST,
                    payload: {
                        customFandeckList: response.data.customFandeckList
                    }
                }
            ];
        case handlers.ADD_CUSTOM_FANDECK:
            if (response.result !== resultCodes.RESULT_OK) {
                if (response.msgParams[0] === resultCodes.ERROR_INTEGRITY) {
                    return [messageHelper.getErrorMessageAction("flashMessage.fandeckExist")];
                }
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_CUSTOM_FANDECKS
                    },
                    method: handlers.GET_ALL_CUSTOM_FANDECKS,
                    tabId: tabId
                },
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.EDIT_CUSTOM_FANDECK_MODAL
                    }
                }
            ];
        case handlers.ADD_CUSTOM_FANDECK_AND_ADD_CUSTOM_FORMULA:
            if (response.result !== resultCodes.RESULT_OK) {
                if (response.msgParams[0] === resultCodes.ERROR_INTEGRITY) {
                    return [messageHelper.getErrorMessageAction("flashMessage.fandeckExist")];
                }

                return [messageHelper.getFlashMessage(response)];
            }

            let newCustomFandeck = modelHelper.mapFandeckToObject(response.data);

            if (newCustomFandeck === null) {
                return [messageHelper.getErrorMessageAction("flashMessage.defaultMessage")];
            }

            if (
                state.formula[tabId] === undefined ||
                state.formula[tabId].product === null ||
                state.formula[tabId].formula === null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.CUSTOM_TAB_AUTOSELECT_FANDECK,
                    payload: {
                        tabId: tabId,
                        fandeck: newCustomFandeck
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_CUSTOM_FANDECKS
                    },
                    method: handlers.GET_ALL_CUSTOM_FANDECKS,
                    tabId: 0
                },
                customTabActions.checkAndAddCustomFormula(
                    tabId,
                    state.formula[tabId].product,
                    newCustomFandeck,
                    state.formula[tabId].formula.color,
                    state.formula[tabId].base,
                    state.settings.formulaCode,
                    state.settings.searchPanelBase
                )
            ];
        case handlers.EDIT_CUSTOM_FANDECK:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_CUSTOM_FANDECKS
                    },
                    method: handlers.GET_ALL_CUSTOM_FANDECKS,
                    tabId: 0
                },
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.EDIT_CUSTOM_FANDECK_MODAL
                    }
                }
            ];
        case handlers.REMOVE_CUSTOM_FANDECK:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_CUSTOM_FANDECKS
                    },
                    method: handlers.GET_ALL_CUSTOM_FANDECKS,
                    tabId: 0
                }
            ];
        case handlers.CUSTOM_TAB_LOAD_BASE_LIST:
            if (!responseHelper.isGetBasesByProductFormatValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                {
                    type: actionTypes.CUSTOM_TAB_SAVE_BASE_LIST,
                    payload: {
                        tabId: tabId,
                        baseList: response.data.baseList
                    }
                }
            ];
        case handlers.CUSTOM_TAB_LOAD_MASTER_PRODUCT_LIST:
            if (!responseHelper.isGetMasterProductListFormatValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                {
                    type: actionTypes.CUSTOM_TAB_SAVE_MASTER_PRODUCT_NAME,
                    payload: {
                        tabId: tabId,
                        masterProductName: response.data.masterProduct ? response.data.masterProduct.displayName : null
                    }
                },
                {
                    type: actionTypes.CUSTOM_TAB_SAVE_MASTER_PRODUCT_LIST,
                    payload: {
                        tabId: tabId,
                        masterProductList: response.data.zoneSiblings
                    }
                }
            ];
        case handlers.CUSTOM_TAB_GET_PACKAGES_BY_PARAMS:
            if (!responseHelper.isGetPackagesByParamsFormatValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            let packageListByParams = modelHelper.mapBippToObjects(response.data.packageList);
            let selectedPackageByParams =
                packageListByParams.find((record) => record.id === state.formula[tabId]?.package?.id) ??
                (packageListByParams.length > 0 ? packageListByParams[0] : null);

            return [
                {
                    type: actionTypes.CUSTOM_TAB_SAVE_PACKAGE_LIST,
                    payload: {
                        tabId: tabId,
                        packageList: packageListByParams,
                        selectedPackage: selectedPackageByParams
                    }
                }
            ];
        case handlers.CHECK_AND_ADD_CUSTOM_FORMULA:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            if (
                state.formula[tabId] === undefined ||
                state.formula[tabId].fandeck === null ||
                state.formula[tabId].formula === null
            ) {
                return [];
            }

            if (response.data === true) {
                return [
                    navigationActions.openConfirmationModal(
                        tabId,
                        customTabActions.checkCustomCifAndAddCustomFormula(
                            tabId,
                            state.formula[tabId].fandeck,
                            state.formula[tabId].formula.color,
                            state.formula[tabId].spectralData?.spectral ?? null,
                            state.settings.formulaCode
                        ),
                        translate("customTab.formulaCombinationCheck", {
                            colorName: state.formula[tabId].formula.color.name
                        }),
                        translate("general.overwrite"),
                        true,
                        true
                    )
                ];
            }

            return [
                customTabActions.checkCustomCifAndAddCustomFormula(
                    tabId,
                    state.formula[tabId].fandeck,
                    state.formula[tabId].formula.color,
                    state.formula[tabId].spectralData?.spectral ?? null,
                    state.settings.formulaCode
                )
            ];
        case handlers.CHECK_CUSTOM_CIF_AND_ADD_CUSTOM_FORMULA:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            if (
                state.formula[tabId] === undefined ||
                state.formula[tabId].fandeck === null ||
                state.formula[tabId].formula === null
            ) {
                return [];
            }

            if (response.data === true) {
                return [
                    navigationActions.openConfirmationModal(
                        tabId,
                        customTabActions.addCustomFormula(tabId),
                        translate("customTab.cifCombinationCheck", {
                            colorName: state.formula[tabId].formula.color.name,
                            fandeckName: state.formula[tabId].fandeck.displayName
                        }),
                        translate("general.overwrite"),
                        true,
                        true
                    )
                ];
            }

            return [customTabActions.addCustomFormula(tabId)];
        case handlers.ADD_CUSTOM_FORMULA:
            if (!responseHelper.isAddCustomFormulaValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            let newCustomColor = modelHelper.mapColorToObject(response.data.colorInFandeck.color);

            if (
                state.formula[tabId] === undefined ||
                state.formula[tabId].product === null ||
                state.formula[tabId].fandeck === null ||
                state.formula[tabId].base === null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.NAVIGATION_SAVE_FORMULA,
                    payload: {
                        tabId: tabId,
                        product: state.formula[tabId].product,
                        fandeck: state.formula[tabId].fandeck,
                        color: newCustomColor,
                        base: state.formula[tabId].base,
                        bipp: state.formula[tabId].package
                    }
                }
            ];
        case handlers.DELETE_CUSTOM_COLOR:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                {
                    type: actionTypes.SEARCH_PANEL_INIT,
                    payload: {
                        tabId: tabId,
                        type: componentTypes.MAIN_TAB,
                        local: true
                    }
                }
            ];
        case handlers.CHECK_AND_ADD_CUSTOM_FORMULA_FROM_XML:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            let checkRequest = state.server.requests.find((request) => request.id === response.sequence);

            if (checkRequest === undefined || checkRequest.next === null) {
                return [];
            }

            if (response.data === true) {
                return [
                    navigationActions.openConfirmationModal(
                        tabId,
                        checkRequest.next,
                        translate("customTab.formulaCombinationCheck", {
                            colorName: checkRequest.next.next !== null ? checkRequest.next.next.color : ""
                        }),
                        translate("general.overwrite"),
                        true,
                        true
                    )
                ];
            }

            return [checkRequest.next];
        case handlers.ADD_CUSTOM_FORMULA_FROM_XML:
            if (!responseHelper.isBooleanResponseValid(response)) {
                let addRequest = state.server.requests.find((request) => request.id === response.sequence);

                if (addRequest === undefined || addRequest.next === null) {
                    return [
                        messageHelper.getErrorMessageAction("flashMessage.customFormulaCreationError"),
                        globalActions.fileTransferFinished()
                    ];
                }

                const invalidColorants = [];

                for (let colorantName of addRequest.next.colorants) {
                    if (state.global.allColorantList.every((colorant) => colorant.name !== colorantName)) {
                        invalidColorants.push(colorantName);
                    }
                }

                if (invalidColorants.length > 0) {
                    return [
                        messageHelper.getErrorMessageAction("mainTab.formulaInvalidColorants", {
                            colorants: invalidColorants
                        }),
                        globalActions.fileTransferFinished()
                    ];
                }

                return [
                    messageHelper.getErrorMessageAction("flashMessage.customFormulaFromXmlCreationError", {
                        ...addRequest.next,
                        colorants: addRequest.next.colorants.join(", ")
                    }),
                    globalActions.fileTransferFinished()
                ];
            }

            return [
                messageHelper.getSuccessMessageAction("flashMessage.customFormulaCreated"),
                globalActions.fileTransferFinished(),
                ticketingActions.getTickets(tabId),
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ALL_CUSTOM_FANDECKS
                    },
                    method: handlers.GET_ALL_CUSTOM_FANDECKS,
                    tabId: tabId
                },
                formulaActions.getFormulaById(tabId, response.data, response)
            ];
        default:
            return [
                {
                    type: actionTypes.UNKNOWN_ACTION
                }
            ];
    }
};
